import {
  Children,
  cloneElement,
  createContext,
  type HTMLAttributes,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { twMerge } from "tailwind-merge"
import { Button, type ButtonProps, type Size } from "../../button/Button"
import { IconButton, type IconButtonProps } from "../../button/IconButton"

const ToggleButtonGroupContext = createContext<{
  selectedKey: string | null
  setSelectedKey: (key: string) => void
}>({
  selectedKey: null,
  setSelectedKey: () => null,
})

export type ToggleButtonProps = Omit<ButtonProps, "variant"> & {
  id: string
  children: React.ReactNode
}

export const ToggleButton = ({
  id,
  children,
  className,
  ...props
}: ToggleButtonProps) => {
  const state = useContext(ToggleButtonGroupContext)
  const isSelected = state.selectedKey === id

  return (
    <Button
      className={twMerge("tt-toggle-group-button", className)}
      variant={isSelected ? "default" : "ghost"}
      onPress={() => {
        state.setSelectedKey(id)
      }}
      {...props}
    >
      {children}
    </Button>
  )
}

export type ToggleIconButtonProps = Omit<
  IconButtonProps,
  "variant" | "label"
> & {
  id: string
  label?: string
}

export const ToggleIconButton = ({
  className,
  id,
  label,
  ...props
}: ToggleIconButtonProps) => {
  const state = useContext(ToggleButtonGroupContext)
  const isSelected = state.selectedKey === id

  return (
    <IconButton
      className={twMerge("tt-toggle-group-icon-button", className)}
      variant={isSelected ? "default" : "ghost"}
      label={label || id}
      onPress={() => {
        state.setSelectedKey(id)
      }}
      {...props}
    />
  )
}

type ToggleButtonGroupProps = HTMLAttributes<HTMLDivElement> & {
  defaultSelectedKey?: string
  onSelectionChange?: (key: string) => void
  selectedKey?: string | null
  size?: Size
}

// TODO: Replace this with react-aria ToggleButtonGroup

/**
 * A way to toggle between multiple options. Also known as a segmented control.
 *
 * ```tsx
 * import { ToggleButtonGroup, ToggleButton } from "@tokenterminal/core/ui/input/toggle/ToggleButtonGroup"
 *
 * <ToggleButtonGroup>
 *   <ToggleButton id="all">All</ToggleButton>
 *   <ToggleButton id="mine">Mine</ToggleButton>
 * </ToggleButtonGroup>
 * ```
 */

export const ToggleButtonGroup = ({
  children,
  className,
  defaultSelectedKey,
  onSelectionChange: _externalOnSelectionChange,
  selectedKey: _externalSelectedKey,
  size = "medium",
  ...props
}: ToggleButtonGroupProps) => {
  const groupRef = useRef<HTMLDivElement>(null)
  const internalState = useState<string | null>(defaultSelectedKey ?? null)
  const selectedKey = _externalSelectedKey ?? internalState[0]
  const setSelectedKey = useCallback(
    (key: string) => {
      internalState[1](key)
      _externalOnSelectionChange?.(key)
    },
    [_externalOnSelectionChange]
  )

  const state = useMemo(
    () => ({
      selectedKey,
      setSelectedKey,
    }),
    [selectedKey, setSelectedKey]
  )

  // Allow user to choose between buttons using left/right arrow keys
  useEffect(() => {
    const group = groupRef.current

    if (!group) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
        return
      }

      // Check if focus is within one of the toggle buttons
      const activeElement = document.activeElement
      if (!activeElement || !(activeElement instanceof Element)) {
        return
      }
      const buttons = Array.from(group.querySelectorAll(".tt-button"))
      const currentIndex = buttons.indexOf(activeElement)

      if (currentIndex === -1) {
        return
      }

      let nextIndex
      if (e.key === "ArrowLeft") {
        nextIndex = Math.max(0, currentIndex - 1)
      } else {
        nextIndex = Math.min(buttons.length - 1, currentIndex + 1)
      }

      if (nextIndex !== currentIndex) {
        e.preventDefault()
        const nextButton = buttons[nextIndex] as HTMLElement
        nextButton.focus()
        nextButton.click()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  return (
    <div
      ref={groupRef}
      className={twMerge("tt-toggle-group", className)}
      data-size={size}
      {...props}
    >
      <ToggleButtonGroupContext.Provider value={state}>
        {Children.map(children, (child) => {
          if (isValidElement(child) && child.type === ToggleButton) {
            const toggleButtonChild =
              child as React.ReactElement<ToggleButtonProps>
            return cloneElement(child, {
              key: toggleButtonChild.props.id,
              size,
            } as Partial<ToggleButtonProps>)
          }
          return child
        })}
      </ToggleButtonGroupContext.Provider>
    </div>
  )
}
